import { firebase } from '@/plugins/firebase';
import * as ivsServices from '@/services/ivsServices';
import * as liveServices from '@/services/liveServices';
import * as reserveServices from '@/services/reserveServices';
import * as userServices from '@/services/userServices';
import * as storageServices from '@/services/storageServices';
import * as freeLiveCheckServices from '@/services/freeLiveCheckServices';
import * as liveReportServices from '@/services/liveReportServices';

const functions = firebase.app().functions('asia-northeast1');

export default {
  async startNewLiveStreaming({ commit }, payload) {
    const storageFolderPath = `lives/${payload.userId}/thumbnails`;
    const thumbnailURL = await storageServices.uploadPhoto(
      storageFolderPath,
      payload.thumbnailFile
    );

    const liveDoc = {
      performerDocRef: userServices.getUserDocRef(payload.userId),
      title: payload.title,
      thumbnailURL: thumbnailURL,
      playbackUrl: payload.playbackUrl,
      startAt: firebase.firestore.FieldValue.serverTimestamp(),
      nowReserveId: null,
      freeViewUsers: [],
      paidViewUsers: [],
    };

    const recentLiveDoc = await liveServices.createRecentLive(liveDoc);
    await userServices.setUserDoc(payload.userId, {
      isStreaming: true,
      recentLiveId: recentLiveDoc.id
    });

    // TODO: create comment realtime database
    commit('live/updateRecentLive', liveDoc);
    commit('user/updateRecentLiveId', recentLiveDoc.id);
    commit('user/updateIsStreaming', true);
  },
  async startLiveStreaming({ commit }, payload) {
    // update user document
    await userServices.setUserDoc(payload.userId, {
      isStreaming: true,
      recentLiveId: payload.liveId
    });

    // update recent live document
    payload.liveDoc.performerDocRef = userServices.getUserDocRef(
      payload.userId
    );
    payload.liveDoc.startAt = firebase.firestore.FieldValue.serverTimestamp();
    await liveServices.updateRecentLiveByLiveId(
      payload.liveId,
      payload.liveDoc
    );

    // delete upcoming live document
    await liveServices.deleteUpComingLiveByLiveId(payload.liveId);

    // commit new state
    commit('live/updateUpComingLive', {});
    commit('live/updateRecentLive', payload.liveDoc);
    commit('user/updateRecentLiveId', payload.liveId);
    commit('user/updateIsStreaming', true);
  },
  async endStreaming({ commit }, payload) {
    const stopStream = functions.httpsCallable('stopStream');
    await stopStream({ channelArn: payload.channelArn });

    // update user document
    await userServices.setUserDoc(payload.userId, {
      isStreaming: false,
      recentLiveId: ''
    });

    await liveReportServices.createLiveReport(payload.liveId);

    // update ended live document
    const liveDoc = {
      ...payload.liveDoc,
      endedAt: firebase.firestore.FieldValue.serverTimestamp()
    };
    delete liveDoc.playbackUrl;
    await liveServices.updateEndedLiveByLiveId(payload.liveId, liveDoc);

    // delete recent live document
    await liveServices.deleteRecentLiveByLiveId(payload.liveId);

    // commit new state
    commit('live/updateRecentLive', {});
    commit('user/updateRecentLiveId', '');
    commit('user/updateIsStreaming', false);
  },
  async resetStreamKey({ commit }, payload) {
    const { streamKey } = await ivsServices.resetStreamKey(
      payload.channelArn,
      payload.streamKeyArn
    );

    await userServices.setUserDoc(payload.userId, {
      ivsStreamKeyArn: streamKey.arn
    });
    commit('ivs/updateStreamKey', streamKey);
    commit('user/updateIvsStreamKeyArn', streamKey.arn);
  },
  async startReserveLive({ commit }, payload) {
    let liveDoc = await liveServices.getRecentLiveByLiveId(payload.liveId);
    let reserveDoc = await reserveServices.getReserveById(payload.liveId, payload.reserveDocId);
    if (!liveDoc || !reserveDoc) {
      return;
    }

    if (!await reserveServices.onAir(reserveDoc)) return;
    commit('live/updateReserveLive', reserveDoc);

    if (!liveDoc.nowReserveId || liveDoc.nowReserveId != payload.reserveId) {
      liveDoc.nowReserveId = payload.reserveId;
      await liveServices.updateRecentLiveByLiveId(
        payload.liveId,
        liveDoc
      );
      commit('live/updateRecentLive', liveDoc);
    }
  },
  async endReserveLive({ commit }, payload) {
    let liveDoc = await liveServices.getRecentLiveByLiveId(payload.liveId);
    let reserveDoc = await reserveServices.getReserveById(payload.liveId, payload.reserveId);
    if (!liveDoc || !reserveDoc) {
      return;
    }

    if (!await reserveServices.terminate(reserveDoc)) return;
    commit('live/updateReserveLive', {});

    if (liveDoc.nowReserveId) {
      liveDoc.nowReserveId = null;
      await liveServices.updateRecentLiveByLiveId(
        payload.liveId,
        liveDoc
      );
      commit('live/updateRecentLive', liveDoc);
    }
  },
  async startFreeView({ commit }, payload) {
    let uid = payload.userId;
    let liveDoc = await liveServices.getRecentLiveByLiveId(payload.liveId);
    if (!liveDoc.freeViewUsers) {
      liveDoc.freeViewUsers = [];
    }

    if (!liveDoc.freeViewUsers.includes(uid)) {
      liveDoc.freeViewUsers.push(uid);
      await liveServices.updateRecentLiveByLiveId(
        payload.liveId,
        liveDoc
      );
      await freeLiveCheckServices.getCount(uid, payload.liveId);
      commit('live/updateRecentLive', liveDoc);
    }
  },
  async startPaidView({ commit }, payload) {
    let uid = payload.userId;
    let liveDoc = await liveServices.getRecentLiveByLiveId(payload.liveId);
    if (!liveDoc.freeViewUsers) {
      liveDoc.freeViewUsers = [];
    }
    if (!liveDoc.paidViewUsers) {
      liveDoc.paidViewUsers = [];
    }

    let freeIdx = liveDoc.freeViewUsers.indexOf(uid);
    if (freeIdx >= 0) {
      liveDoc.freeViewUsers.splice(freeIdx, 1);
    }

    if (!liveDoc.paidViewUsers.includes(uid)) {
      liveDoc.paidViewUsers.push(uid);
      await liveServices.updateRecentLiveByLiveId(
        payload.liveId,
        liveDoc
      );
      commit('live/updateRecentLive', liveDoc);
    }
  },
  async checkReserveLive({ commit }, payload) {
    if (!payload.nowReserveId) {
      commit('live/updateReserveLive', {});
      return;
    }
    commit('live/updateReserveLive', await reserveServices.getReserveById(payload.liveId, payload.nowReserveId));
  },
  async checkIsStreaming({ commit }, userIdArg) {
    let userId = userIdArg;
    const recentLives = await liveServices.getRecentLivesByPerformerId(userId, 1);
    const user = await userServices.getUserDoc(userId);

    let isStreaming = recentLives && recentLives.length > 0;
    if (isStreaming == user.isStreaming) {
      return;
    }

    let payload = {
      isStreaming: isStreaming,
      recentLiveId: isStreaming ? recentLives[0].id : ''
    }

    await userServices.setUserDoc(userId, payload);
    commit('user/updateIsStreaming', isStreaming);
  }
};
